interface AppConfigEntry {
  data?: Record<string, unknown>;
}

declare global {
  interface Window {
    __APP_CONFIG__?: unknown;
    __APP_INJECTED_RUNTIME_CONFIG__?: unknown;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isAppConfigEntry(value: unknown): value is AppConfigEntry {
  if (!isRecord(value)) {
    return false;
  }

  return value.data === undefined || isRecord(value.data);
}

function toAppConfigEntry(value: Record<string, unknown>): AppConfigEntry {
  if (isAppConfigEntry(value) && value.data !== undefined) {
    return value;
  }

  return { data: value };
}

function isBlockedPathSegment(segment: string): boolean {
  return (
    segment === '__proto__' ||
    segment === 'constructor' ||
    segment === 'prototype'
  );
}

function toConfigEntries(value: unknown): AppConfigEntry[] {
  return [...(value as unknown[])].filter(isAppConfigEntry).reverse();
}

function parseJsonConfig(rawConfig: string): unknown {
  try {
    return JSON.parse(rawConfig);
  } catch (error) {
    throw new Error(
      `Invalid APP_CONFIG: ${
        error instanceof Error ? error.message : 'JSON parse failed'
      }`,
      { cause: error },
    );
  }
}

function readOwnConfigValue(current: unknown, segment: string): unknown {
  if (isBlockedPathSegment(segment) || !isRecord(current)) {
    return undefined;
  }

  return Object.getOwnPropertyDescriptor(current, segment)?.value;
}

let cachedRawConfig: unknown;
let cachedConfigEntries: AppConfigEntry[] | undefined;

function readRawAppConfig(): unknown {
  if (typeof window !== 'undefined') {
    return (
      window.__APP_INJECTED_RUNTIME_CONFIG__ ??
      window.__APP_CONFIG__ ??
      (process.env.APP_CONFIG as unknown)
    );
  }

  return process.env.APP_CONFIG as unknown;
}

function parseRawObjectConfig(rawConfig: Record<string, unknown>) {
  cachedConfigEntries = [toAppConfigEntry(rawConfig)];
  return cachedConfigEntries;
}

function parseAppConfig(): AppConfigEntry[] {
  const rawConfig = readRawAppConfig();

  if (rawConfig === cachedRawConfig && cachedConfigEntries) {
    return cachedConfigEntries;
  }

  cachedRawConfig = rawConfig;

  if (Array.isArray(rawConfig)) {
    cachedConfigEntries = toConfigEntries(rawConfig);
    return cachedConfigEntries;
  }

  if (isRecord(rawConfig)) {
    return parseRawObjectConfig(rawConfig);
  }

  if (typeof rawConfig === 'string' && rawConfig.trim()) {
    const parsed = parseJsonConfig(rawConfig);
    if (!Array.isArray(parsed)) {
      throw new Error('Invalid APP_CONFIG: expected array of AppConfigEntry');
    }

    cachedConfigEntries = toConfigEntries(parsed);
    return cachedConfigEntries;
  }

  cachedConfigEntries = [];
  return cachedConfigEntries;
}

export function readFrontendConfigValue(path: string): unknown {
  const segments = path.split('.');

  if (segments.some(isBlockedPathSegment)) {
    return undefined;
  }

  const configEntries = parseAppConfig();

  for (const entry of configEntries) {
    let current: unknown = entry.data;

    for (const segment of segments) {
      current = readOwnConfigValue(current, segment);
      if (current === undefined) {
        current = undefined;
        break;
      }
    }

    if (current !== undefined) {
      return current;
    }
  }

  return undefined;
}

export function readConfigBoolean(
  path: string,
  defaultValue: boolean,
): boolean {
  const value = readFrontendConfigValue(path);

  if (value === undefined) {
    return defaultValue;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === 'true') {
      return true;
    }
    if (normalizedValue === 'false') {
      return false;
    }
  }

  throw new Error(`Invalid APP_CONFIG: ${path} must be a boolean`);
}

export function readConfigString(path: string, defaultValue: string): string {
  const value = readFrontendConfigValue(path);

  if (value === undefined) {
    return defaultValue;
  }
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  throw new Error(`Invalid APP_CONFIG: ${path} must be a non-empty string`);
}

export function readConfigNumber(path: string, defaultValue: number): number {
  const value = readFrontendConfigValue(path);

  if (value === undefined) {
    return defaultValue;
  }

  if (typeof value === 'string' && value.trim() === '') {
    throw new Error(`Invalid APP_CONFIG: ${path} must be a finite number`);
  }

  const numericValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : NaN;

  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  throw new Error(`Invalid APP_CONFIG: ${path} must be a finite number`);
}
